"use client";

import { useEffect, useRef, useCallback } from "react";

interface Particle {
    x: number;
    y: number;
    vx: number;
    vy: number;
    radius: number;
    opacity: number;
}

const PARTICLE_COUNT = 80;
const CONNECTION_DISTANCE = 150;
const MOUSE_RADIUS = 200;
const PARTICLE_SPEED = 0.4;
const BASE_PARTICLE_COLOR = [0, 245, 212]; // cyan-ish
const ACCENT_PARTICLE_COLOR = [139, 92, 246]; // violet

export function NeuralBackground() {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const particlesRef = useRef<Particle[]>([]);
    const mouseRef = useRef({ x: -9999, y: -9999 });
    const animationRef = useRef<number>(0);
    const dimensionsRef = useRef({ width: 0, height: 0 });

    const createParticles = useCallback((width: number, height: number) => {
        const particles: Particle[] = [];
        for (let i = 0; i < PARTICLE_COUNT; i++) {
            particles.push({
                x: Math.random() * width,
                y: Math.random() * height,
                vx: (Math.random() - 0.5) * PARTICLE_SPEED * 2,
                vy: (Math.random() - 0.5) * PARTICLE_SPEED * 2,
                radius: Math.random() * 2 + 1,
                opacity: Math.random() * 0.5 + 0.3,
            });
        }
        return particles;
    }, []);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const ctx = canvas.getContext("2d", { alpha: true });
        if (!ctx) return;

        const handleResize = () => {
            const dpr = window.devicePixelRatio || 1;
            const width = window.innerWidth;
            const height = window.innerHeight;

            canvas.width = width * dpr;
            canvas.height = height * dpr;
            canvas.style.width = `${width}px`;
            canvas.style.height = `${height}px`;
            ctx.scale(dpr, dpr);

            dimensionsRef.current = { width, height };

            if (particlesRef.current.length === 0) {
                particlesRef.current = createParticles(width, height);
            }
        };

        const handleMouseMove = (e: MouseEvent) => {
            mouseRef.current = { x: e.clientX, y: e.clientY };
        };

        const handleMouseLeave = () => {
            mouseRef.current = { x: -9999, y: -9999 };
        };

        const drawParticle = (p: Particle, index: number) => {
            const colorMix = index / PARTICLE_COUNT;
            const r = Math.round(
                BASE_PARTICLE_COLOR[0] * (1 - colorMix) +
                ACCENT_PARTICLE_COLOR[0] * colorMix
            );
            const g = Math.round(
                BASE_PARTICLE_COLOR[1] * (1 - colorMix) +
                ACCENT_PARTICLE_COLOR[1] * colorMix
            );
            const b = Math.round(
                BASE_PARTICLE_COLOR[2] * (1 - colorMix) +
                ACCENT_PARTICLE_COLOR[2] * colorMix
            );

            // Glow effect
            const gradient = ctx.createRadialGradient(
                p.x,
                p.y,
                0,
                p.x,
                p.y,
                p.radius * 4
            );
            gradient.addColorStop(0, `rgba(${r}, ${g}, ${b}, ${p.opacity})`);
            gradient.addColorStop(0.5, `rgba(${r}, ${g}, ${b}, ${p.opacity * 0.3})`);
            gradient.addColorStop(1, `rgba(${r}, ${g}, ${b}, 0)`);

            ctx.beginPath();
            ctx.arc(p.x, p.y, p.radius * 4, 0, Math.PI * 2);
            ctx.fillStyle = gradient;
            ctx.fill();

            // Core dot
            ctx.beginPath();
            ctx.arc(p.x, p.y, p.radius, 0, Math.PI * 2);
            ctx.fillStyle = `rgba(${r}, ${g}, ${b}, ${p.opacity + 0.2})`;
            ctx.fill();
        };

        const drawConnection = (
            p1: Particle,
            p2: Particle,
            distance: number
        ) => {
            const opacity = (1 - distance / CONNECTION_DISTANCE) * 0.4;
            const gradient = ctx.createLinearGradient(p1.x, p1.y, p2.x, p2.y);
            gradient.addColorStop(
                0,
                `rgba(${BASE_PARTICLE_COLOR.join(",")}, ${opacity})`
            );
            gradient.addColorStop(
                1,
                `rgba(${ACCENT_PARTICLE_COLOR.join(",")}, ${opacity})`
            );

            ctx.beginPath();
            ctx.moveTo(p1.x, p1.y);
            ctx.lineTo(p2.x, p2.y);
            ctx.strokeStyle = gradient;
            ctx.lineWidth = opacity * 1.5;
            ctx.stroke();
        };

        const animate = () => {
            const { width, height } = dimensionsRef.current;
            if (width === 0) {
                animationRef.current = requestAnimationFrame(animate);
                return;
            }

            ctx.clearRect(0, 0, width, height);

            const particles = particlesRef.current;
            const mouse = mouseRef.current;

            // Update positions
            for (const p of particles) {
                p.x += p.vx;
                p.y += p.vy;

                // Bounce off edges smoothly
                if (p.x < 0) {
                    p.x = 0;
                    p.vx *= -1;
                } else if (p.x > width) {
                    p.x = width;
                    p.vx *= -1;
                }
                if (p.y < 0) {
                    p.y = 0;
                    p.vy *= -1;
                } else if (p.y > height) {
                    p.y = height;
                    p.vy *= -1;
                }

                // Mouse interaction — gentle repulsion
                const dx = p.x - mouse.x;
                const dy = p.y - mouse.y;
                const dist = Math.sqrt(dx * dx + dy * dy);
                if (dist < MOUSE_RADIUS && dist > 0) {
                    const force = (MOUSE_RADIUS - dist) / MOUSE_RADIUS;
                    p.vx += (dx / dist) * force * 0.02;
                    p.vy += (dy / dist) * force * 0.02;
                }

                // Dampen velocity
                p.vx *= 0.999;
                p.vy *= 0.999;

                // Ensure minimum speed
                const speed = Math.sqrt(p.vx * p.vx + p.vy * p.vy);
                if (speed < PARTICLE_SPEED * 0.3) {
                    p.vx += (Math.random() - 0.5) * 0.05;
                    p.vy += (Math.random() - 0.5) * 0.05;
                }
            }

            // Draw connections
            for (let i = 0; i < particles.length; i++) {
                for (let j = i + 1; j < particles.length; j++) {
                    const dx = particles[i].x - particles[j].x;
                    const dy = particles[i].y - particles[j].y;
                    const distance = Math.sqrt(dx * dx + dy * dy);

                    if (distance < CONNECTION_DISTANCE) {
                        drawConnection(particles[i], particles[j], distance);
                    }
                }
            }

            // Draw mouse connections
            if (mouse.x > -9000) {
                for (const p of particles) {
                    const dx = p.x - mouse.x;
                    const dy = p.y - mouse.y;
                    const distance = Math.sqrt(dx * dx + dy * dy);

                    if (distance < MOUSE_RADIUS) {
                        const opacity = (1 - distance / MOUSE_RADIUS) * 0.6;
                        ctx.beginPath();
                        ctx.moveTo(mouse.x, mouse.y);
                        ctx.lineTo(p.x, p.y);
                        ctx.strokeStyle = `rgba(0, 245, 212, ${opacity})`;
                        ctx.lineWidth = opacity * 2;
                        ctx.stroke();
                    }
                }
            }

            // Draw particles
            particles.forEach((p, i) => drawParticle(p, i));

            animationRef.current = requestAnimationFrame(animate);
        };

        handleResize();
        window.addEventListener("resize", handleResize);
        window.addEventListener("mousemove", handleMouseMove);
        document.addEventListener("mouseleave", handleMouseLeave);
        animationRef.current = requestAnimationFrame(animate);

        return () => {
            window.removeEventListener("resize", handleResize);
            window.removeEventListener("mousemove", handleMouseMove);
            document.removeEventListener("mouseleave", handleMouseLeave);
            cancelAnimationFrame(animationRef.current);
        };
    }, [createParticles]);

    return (
        <canvas
            ref={canvasRef}
            className="fixed inset-0 -z-10 pointer-events-none"
            style={{ background: "transparent" }}
            aria-hidden="true"
        />
    );
}
